import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'

import { BaseAddress as AddressType } from '@lib/graphql/graphql'
import { openModalView } from '@redux/modalSlice'
import clsx from 'clsx'
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { HiOutlineDotsVertical } from 'react-icons/hi'
import { IoCardOutline } from 'react-icons/io5'
import { MdOutlineLocalShipping } from 'react-icons/md'
import { useDispatch } from 'react-redux'
import DeleteAddressForm from '../../DeleteAddressForm/DeleteAddressForm'
import s from '../M61ProfileView.module.scss'

interface Props {
  className?: string
  address: AddressType
  isDefaultBilling: boolean
  isDefaultShipping: boolean
  index: number
}
type NativeAttrs = Omit<React.HTMLAttributes<HTMLDivElement>, keyof Props>

export type AddressProps = Props & NativeAttrs

const Address = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<AddressProps>
>(
  (
    {
      className,
      address,
      index,
      isDefaultBilling,
      isDefaultShipping,
      children,
      ...props
    },
    ref,
  ) => {
    const dispatch = useDispatch()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    // click outside to close dropdown
    const dropdownRef = useRef(null)
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    const handleEditClick = () => {
      dispatch(
        openModalView({
          modalView: 'ADDRESS_MODAL',
          props: {
            data: {
              type: 'update',
              address,
              isDefaultBilling: isDefaultBilling,
              isDefaultShipping: isDefaultShipping,
            },
          },
        }),
      )
      setIsDropdownOpen(false)
    }

    const handleDeleteClick = () => {
      setIsDropdownOpen(false)
    }

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    })

    const addressName =
      address.metadata?.find((x) => x.key === 'name')?.value ?? null

    return (
      <div key={address.id} className={s.address} {...props} ref={ref}>
        <div className={s.address__title}>
          <span>
            {addressName !== '' ? addressName : 'Address #' + (index + 1)}
            {isDefaultShipping && isDefaultBilling ? (
              <>
                <div className={s.tooltipWrapper}>
                  <IoCardOutline />
                  <div className={s.tooltip}>
                    <p>Default billing</p>
                  </div>
                </div>
                <div className={s.tooltipWrapper}>
                  <MdOutlineLocalShipping />
                  <div className={s.tooltip}>
                    <p>Default shipping</p>
                  </div>
                </div>
              </>
            ) : isDefaultBilling ? (
              <div className={s.tooltipWrapper}>
                <IoCardOutline />
                <div className={s.tooltip}>
                  <p>Default billing</p>
                </div>
              </div>
            ) : (
              isDefaultShipping && (
                <div className={s.tooltipWrapper}>
                  <MdOutlineLocalShipping />
                  <div className={s.tooltip}>
                    <p>Default shipping</p>
                  </div>
                </div>
              )
            )}
          </span>
          <div className={s.address__edit}>
            <div
              className={clsx(s.dropdownWrapper, {
                [s.dropdownWrapper__open]: isDropdownOpen,
              })}
              ref={dropdownRef}
            >
              <div className={s.dropdownItem}>
                <a onClick={handleEditClick}>Edit</a>
              </div>
              <div className={s.dropdownItem}>
                <DeleteAddressForm
                  address={address}
                  isDefaultBilling={isDefaultBilling}
                  isDefaultShipping={isDefaultShipping}
                  onDeleteClick={handleDeleteClick}
                />
              </div>
            </div>
            <div
              className={s.dropdownIcon}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <HiOutlineDotsVertical />
            </div>
          </div>
        </div>

        <TrustedHtml
          className={s.address__content}
          html={address.formatted?.replaceAll('\n', '<br />')}
        />
      </div>
    )
  },
)

Address.displayName = 'Address'
export default Address
